import { countBy } from "lodash-es";
import { uniqBy, range } from "../utils/array.ts";
import { z } from "zod";
import type { Bitmap, WritableBitmap } from "../bitmap/types.ts";
import { bitmapChannels } from "../bitmap/types.ts";
import type { DistanceSize } from "./distance.ts";
import { distanceFromMillimeters } from "./distance.ts";
import type { GenericPosition, GenericSize } from "./geom.ts";
import { baseplateSizeSchema } from "./project-primitives.ts";
import type { BrickColourHexString, BrickColour } from "./bricks.ts";
import { brickColourSchema } from "./bricks.ts";
import { weightFromG } from "./weight.ts";
import { readUInt32BE } from "../utils/uint8-array.ts";

type PaletteItem = {
	readonly brick: BrickColour;
	readonly limit: number | undefined;
};

type Palette = readonly PaletteItem[];

const autoPaletteModeSchema = z.object({
	type: z.literal("auto"),
	numberOfColours: z.number().int().nonnegative(),
});

const customPaletteModeSchema = z.object({
	type: z.literal("custom"),
	palette: z.array(brickColourSchema).readonly(),
});

const hybridPaletteModeSchema = z.object({
	type: z.literal("hybrid"),
	palette: z.array(brickColourSchema).readonly(),
	numberOfExtraColours: z.number().int().nonnegative(),
});

type CustomPaletteMode = z.infer<typeof customPaletteModeSchema>;

type AutoPaletteMode = z.infer<typeof autoPaletteModeSchema>;

type HybridPaletteMode = z.infer<typeof hybridPaletteModeSchema>;

type PaletteMode = AutoPaletteMode | CustomPaletteMode | HybridPaletteMode;

const genericPositionNumber: z.Schema<GenericPosition<number>> = z.object({
	x: z.number().int(),
	y: z.number().int(),
});

type BrickPosition = GenericPosition<number>;

const penMarkSchema = z
	.object({
		position: genericPositionNumber,
		colour: brickColourSchema,
	})
	.readonly();

type PenMark = z.infer<typeof penMarkSchema>;

const maxImageZoom = 5;

type NumberOfBricks = GenericSize<number>;
type ImageZoomOffset = GenericPosition<number>;

type PenMarkings = readonly PenMark[];

function getPenColoursSet(pen: PenMarkings): ReadonlySet<BrickColour> {
	return new Set(
		uniqBy(
			pen.map((b) => b.colour),
			(c) => c.rgba,
		),
	);
}

const genericSizeNumber: z.Schema<GenericSize<number>> = z.object({
	width: z.number().nonnegative(),
	height: z.number().nonnegative(),
});

const detailFilterSchema = z
	.object({
		strength: z.number().min(0).max(100),
		radiusRatio: z.number().min(0).max(1),
	})
	.readonly();

type DetailFilter = z.output<typeof detailFilterSchema>;

/**
 * Options for Floyd Steinberg dithering
 */
const ditheringSchema = z
	.object({
		serpentine: z.boolean(),
		/** Error clamp per channel (normalized); legal: 0..0.5. Recommended default: 0.25 */
		// clamp: z.number().min(0).max(0.5),
		// Client wanted fixed to 0.25
		clamp: z.literal(0.25),
		/** Error scaling multiplier; legal: 0..2. Recommended default: 1 */
		// Client only wanted values 0 - 1
		strength: z.number().min(0).max(1),
	})
	.readonly();

type Dithering = z.output<typeof ditheringSchema>;

function ditheringNumberToPictureValue(
	dithering: number,
): Dithering | undefined {
	if (dithering === 0) {
		return undefined;
	}
	return { clamp: 0.25, strength: dithering, serpentine: false };
}

function ditherPictureValueToNumber(value: Dithering | undefined) {
	if (value === undefined) {
		return 0;
	}
	return value.strength;
}

const pictureConfigurationSchema = z
	.object({
		imageZoom: z.number(),
		imageZoomOffset: genericPositionNumber,

		basePlateSize: baseplateSizeSchema,
		numberOfBasePlates: genericSizeNumber,
		// Derived from basePlateSize and numberOfBasePlates to save on computation
		/** @deprecated Very small computation cost where needed.
		 * Big potential issues trying to keep in sync */
		numberOfBricks: genericSizeNumber,

		removeBackground: z
			.union([
				z
					.object({
						newColour: brickColourSchema,
					})
					.readonly(),
				z
					.object({
						noBrick: z.literal(true),
					})
					.readonly(),
				z
					.object({
						// Admin managed background image. The bitmap and its essential
						// colours arrive via build sources - records are immutable so the
						// id alone pins them.
						backgroundImageId: z.string(),
					})
					.readonly(),
			])
			.optional(),
		enhanceFaces: z.boolean().optional(),
		fixFaceColours: z.boolean().optional(),
		posterise: z.boolean().optional(),

		brightness: z.number(),
		contrast: z.number(),
		saturation: z.number(),

		detailFilter: detailFilterSchema.optional(),
		dithering: ditheringSchema.optional(),

		paletteMode: z.union([
			autoPaletteModeSchema,
			customPaletteModeSchema,
			hybridPaletteModeSchema,
		]),

		pen: z.array(penMarkSchema).readonly(),
		// Note: doesn't really act how we'd want until zod v4. i.e. it's optional still
		border: z
			.union([
				z
					.object({
						size: z.number().int().positive(),
						colour: brickColourSchema,
					})
					.readonly(),
				z.undefined(),
			])
			.readonly(),

		updatedAt: z.number().int().positive(),
	})
	.readonly();

type PictureConfiguration = z.infer<typeof pictureConfigurationSchema>;

type BrickCount = {
	readonly type: "pen-only" | "remove-background-only" | "palette";
	readonly count: number;
};

type BrickCounts = Readonly<Record<BrickColourHexString, BrickCount>>;

type OperationMissingSourcesName =
	| "Fix face colours"
	| "Enhance faces"
	| "Posterise"
	| "Remove background";

type BuildBitmap = Bitmap & {
	readonly noBrickColour: Pick<BrickColour, "rgba"> | undefined;
};

type WritableBuildBitmap = WritableBitmap & Pick<BuildBitmap, "noBrickColour">;

type BrickedPictureOutput = {
	readonly image: BuildBitmap;
	readonly operationsMissingSources: readonly OperationMissingSourcesName[];
	readonly brickCounts: BrickCounts;
};

const realWorldBrickSize = distanceFromMillimeters(8);
const brickWeight = weightFromG(0.18);
const notchRadius = distanceFromMillimeters(4.8);
const notchRidgeWidth = distanceFromMillimeters(0.23);

// Keep dependencies to individual properties to allow lighter hook dependency checks
function realWorldPictureSize(numberOfBricks: NumberOfBricks): DistanceSize {
	return {
		width: distanceFromMillimeters(
			numberOfBricks.width * realWorldBrickSize.mm,
		),
		height: distanceFromMillimeters(
			numberOfBricks.height * realWorldBrickSize.mm,
		),
	};
}

type Option<T> = {
	readonly value: T;
	readonly label: string;
	readonly disabled?: boolean;
};

type OptionsList<T> = readonly Option<T>[];

function numberOfBrickColours(brickCounts: BrickCounts): number {
	return Object.values(brickCounts).filter(({ count }) => count > 0).length;
}

function numberOfBrickColoursInBuildImage(image: Bitmap): number {
	return Object.keys(
		countBy(
			range(0, image.width * image.height).map((i) =>
				readUInt32BE(image.data, i * bitmapChannels),
			),
		),
	).length;
}

function countTotalNumberOfBricks({
	basePlateSize,
	numberOfBasePlates,
}: Pick<PictureConfiguration, "basePlateSize" | "numberOfBasePlates">) {
	return (
		numberOfBasePlates.width *
		basePlateSize *
		numberOfBasePlates.height *
		basePlateSize
	);
}

export type {
	PictureConfiguration,
	DetailFilter,
	BrickCount,
	BrickCounts,
	OptionsList,
	Option,
	BrickedPictureOutput,
	CustomPaletteMode,
	AutoPaletteMode,
	PaletteMode,
	Palette,
	PenMark,
	BrickPosition,
	NumberOfBricks,
	ImageZoomOffset,
	PenMarkings,
	HybridPaletteMode,
	OperationMissingSourcesName,
	BuildBitmap,
	WritableBuildBitmap,
	Dithering,
};
export {
	ditheringNumberToPictureValue,
	ditherPictureValueToNumber,
	ditheringSchema,
	notchRidgeWidth,
	getPenColoursSet,
	maxImageZoom,
	realWorldBrickSize,
	brickWeight,
	notchRadius,
	detailFilterSchema,
	pictureConfigurationSchema,
	realWorldPictureSize,
	countTotalNumberOfBricks,
	numberOfBrickColours,
	numberOfBrickColoursInBuildImage,
};
